/**
 * A free-text field for several labels (an asset's partners): each chosen label is a chip with a remove button, and
 * the text input suggests the labels already in use. Any name may be typed; Enter or a comma adds it. The input is an
 * ARIA combobox over a listbox (the list sits under the field, so it needs no `usePopover`, which opens from a button
 * and takes focus away from the text). The caller's `<label htmlFor={id}>` names it.
 */

import { useId, useRef } from 'react';
import { X } from 'lucide-react';

import { FieldError, fieldA11y } from './FieldError';
import { LABEL_MAX, useLabelInput } from './labelRules';

function Chips({ value, onRemove }: { value: string[]; onRemove: (name: string) => void }) {
  if (value.length === 0) return null;
  return (
    <ul className="label-input-chips">
      {value.map((name) => (
        <li key={name} className="label-input-chip">
          <span>{name}</span>
          <button type="button" aria-label={`Remove ${name}`} onClick={() => onRemove(name)}>
            <X aria-hidden="true" />
          </button>
        </li>
      ))}
    </ul>
  );
}

export function LabelInput({
  id,
  value,
  onChange,
  suggestions,
  maxLength = LABEL_MAX,
  placeholder,
  describedBy,
  error,
}: {
  /** The text input's id, for the field's label. */
  id: string;
  value: string[];
  onChange: (value: string[]) => void;
  /** Labels already in use, offered as the text is typed. */
  suggestions: string[];
  maxLength?: number;
  placeholder?: string;
  describedBy?: string;
  /** What is wrong with the labels, shown under the field and tied to the text input. */
  error?: string | null;
}) {
  const listId = useId();
  const input = useRef<HTMLInputElement>(null);
  const state = useLabelInput({ value, onChange, suggestions, maxLength });
  const optionId = (i: number) => `${listId}-${i}`;

  return (
    <>
      <div className="label-input-wrap">
        {/* A click on the box's free space lands in the text input, as on a native field. */}
        <div className="label-input" onClick={() => input.current?.focus()}>
          <Chips
            value={value}
            onRemove={(name) => {
              state.remove(name);
              input.current?.focus();
            }}
          />
          <input
            ref={input}
            id={id}
            type="text"
            role="combobox"
            autoComplete="off"
            maxLength={maxLength}
            placeholder={value.length === 0 ? placeholder : undefined}
            {...fieldA11y(id, error, describedBy)}
            aria-autocomplete="list"
            aria-expanded={state.shown}
            aria-controls={listId}
            aria-activedescendant={state.shown && state.active >= 0 ? optionId(state.active) : undefined}
            value={state.text}
            onChange={(e) => state.type(e.target.value)}
            onKeyDown={state.onKeyDown}
            onClick={state.open}
            onBlur={state.blur}
          />
        </div>
        <span className="sr-only" role="status">
          {state.shown ? `${state.options.length} ${state.options.length === 1 ? 'suggestion' : 'suggestions'}` : ''}
        </span>
        <ul id={listId} role="listbox" className="user-dropdown label-input-list" hidden={!state.shown}>
          {state.shown &&
            state.options.map((option, i) => (
              <li
                key={`${option.isNew}-${option.value}`}
                id={optionId(i)}
                role="option"
                aria-selected={i === state.active}
                className={`dropdown-item${i === state.active ? ' is-active' : ''}`}
                // Keeps focus (and the typed text) in the input; the click then adds the option.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => state.pick(option)}
              >
                {option.isNew ? `Add "${option.value}"` : option.value}
              </li>
            ))}
        </ul>
      </div>
      <FieldError inputId={id} message={error} />
    </>
  );
}
